/**
 * RPC 客户端（主线程侧）：call 发起请求/等响应，onPush 订阅 Worker 推送。
 * 泛型参数绑定方法契约与推送契约，调用处拿到端到端类型推断。
 */
import {
  RPC_ERROR_CODES,
  RpcError,
  parseWireMessage,
  type MethodMap,
  type PushMap,
  type RpcPort,
  type WireRequest,
} from './protocol';

export interface CallOptions {
  /** 超时毫秒数；0 = 不超时。默认 DEFAULT_CALL_TIMEOUT_MS。 */
  timeoutMs?: number;
  /** 随请求转移所有权的对象（如上传的 ArrayBuffer）。 */
  transfer?: Transferable[];
}

export const DEFAULT_CALL_TIMEOUT_MS = 15_000;

export interface RpcClient<M extends MethodMap, P extends PushMap> {
  call<K extends keyof M & string>(
    method: K,
    payload: M[K]['req'],
    options?: CallOptions,
  ): Promise<M[K]['res']>;
  /** 返回取消订阅函数。同一事件可多处订阅；无订阅者的推送被丢弃。 */
  onPush<E extends keyof P & string>(event: E, handler: (payload: P[E]) => void): () => void;
  /** 拒绝所有在途调用（code = rpc/disposed）并停止监听。 */
  dispose(): void;
}

interface PendingCall {
  resolve(value: unknown): void;
  reject(reason: RpcError): void;
  timer: ReturnType<typeof setTimeout> | undefined;
}

export function createRpcClient<M extends MethodMap, P extends PushMap>(
  port: RpcPort,
): RpcClient<M, P> {
  const pending = new Map<number, PendingCall>();
  const pushHandlers = new Map<string, Set<(payload: unknown) => void>>();
  let nextId = 1;
  let disposed = false;

  function settle(id: number): PendingCall | undefined {
    const entry = pending.get(id);
    if (!entry) return undefined;
    pending.delete(id);
    if (entry.timer !== undefined) clearTimeout(entry.timer);
    return entry;
  }

  function onMessage(ev: MessageEvent): void {
    const msg = parseWireMessage(ev.data);
    if (!msg) return;
    if (msg.kind === 'res') {
      // 超时/dispose 后迟到的响应：settle 查不到条目，静默丢弃
      const entry = settle(msg.id);
      if (!entry) return;
      if (msg.ok) {
        entry.resolve(msg.payload);
      } else {
        entry.reject(new RpcError(msg.error.code, msg.error.message));
      }
      return;
    }
    if (msg.kind === 'push') {
      const handlers = pushHandlers.get(msg.event);
      if (!handlers) return;
      for (const handler of [...handlers]) {
        handler(msg.payload);
      }
    }
    // kind === 'req'：客户端不受理请求，丢弃
  }

  port.addEventListener('message', onMessage);
  port.start?.();

  return {
    call(method, payload, options) {
      if (disposed) {
        return Promise.reject(
          new RpcError(RPC_ERROR_CODES.disposed, `call ${method} on disposed client`),
        );
      }
      const id = nextId++;
      const timeoutMs = options?.timeoutMs ?? DEFAULT_CALL_TIMEOUT_MS;
      return new Promise((resolve, reject) => {
        const timer =
          timeoutMs > 0
            ? setTimeout(() => {
                settle(id);
                reject(new RpcError(RPC_ERROR_CODES.timeout, `${method} timed out (${timeoutMs}ms)`));
              }, timeoutMs)
            : undefined;
        pending.set(id, { resolve, reject, timer });
        const request: WireRequest = { kind: 'req', id, method, payload };
        port.postMessage(request, options?.transfer);
      });
    },

    onPush(event, handler) {
      let handlers = pushHandlers.get(event);
      if (!handlers) {
        handlers = new Set();
        pushHandlers.set(event, handlers);
      }
      const wrapped = handler as (payload: unknown) => void;
      handlers.add(wrapped);
      return () => {
        handlers.delete(wrapped);
      };
    },

    dispose() {
      if (disposed) return;
      disposed = true;
      port.removeEventListener('message', onMessage);
      for (const [id, entry] of [...pending]) {
        pending.delete(id);
        if (entry.timer !== undefined) clearTimeout(entry.timer);
        entry.reject(new RpcError(RPC_ERROR_CODES.disposed, 'rpc client disposed'));
      }
      pushHandlers.clear();
    },
  };
}
